import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, query } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatAmount, formatMoney, formatRelative, methodLabel } from "../../lib/format";
import type { Balance, Payment, Stats } from "../../lib/types";
import {
  EmptyState,
  PageHeader,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";
import { MethodBreakdown, StatTile, SuccessRate, VolumeChart } from "../../components/charts";
import type { DailyPoint, MethodPoint } from "../../components/charts";

interface OverviewResponse {
  mode: string;
  stats: Stats[] | null;
  balances: Balance[] | null;
  recent_payments: Payment[] | null;
}

interface VolumeResponse {
  currency: string;
  series: DailyPoint[] | null;
  breakdown: MethodPoint[] | null;
}

export function Overview() {
  const { mode, tenant } = useAuth();

  const overview = useQuery({
    queryKey: ["overview", mode],
    queryFn: () => api.get<OverviewResponse>(`/api/dashboard/overview${query({ mode })}`),
    refetchInterval: 30_000,
  });

  const volume = useQuery({
    queryKey: ["volume", mode],
    queryFn: () => api.get<VolumeResponse>(`/api/dashboard/volume${query({ mode, days: 30, currency: "USD" })}`),
  });

  const stats = overview.data?.stats ?? [];
  const usd = stats.find((s) => s.currency === "USD");
  const recent = overview.data?.recent_payments ?? [];
  const balances = overview.data?.balances ?? [];

  return (
    <>
      <PageHeader
        eyebrow={mode === "live" ? "Live" : "Sandbox"}
        title={greeting(tenant?.trading_name || tenant?.legal_name)}
        description="Everything collected, owed and settled over the last thirty days."
      />

      {tenant && !tenant.can_transact_live && <VerificationPrompt status={tenant.status} />}

      {/* Figures first: the four numbers a merchant opens the dashboard to see. */}
      <div className="settle settle-1 mb-6 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-paper-raised">
          <StatTile
            label="Collected"
            value={usd ? formatMoney(usd.gross_minor, "USD") : "USD 0.00"}
            detail="Charged to your customers"
          />
        </div>
        <div className="bg-paper-raised">
          <StatTile
            label="Platform fees"
            value={usd ? formatMoney(usd.fees_minor, "USD") : "USD 0.00"}
            detail="What PayFlow charged you"
            accent="brass"
          />
        </div>
        <div className="bg-paper-raised">
          <StatTile
            label="Net to you"
            value={usd ? formatMoney(usd.net_minor, "USD") : "USD 0.00"}
            detail="After platform and bank fees"
            accent="verdigris"
          />
        </div>
        <div className="bg-paper-raised">
          <SuccessRate succeeded={usd?.succeeded_count ?? 0} failed={usd?.failed_count ?? 0} />
        </div>
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Sheet className="settle settle-2 overflow-hidden">
          {volume.isLoading ? (
            <Skeleton rows={4} />
          ) : (
            <VolumeChart data={volume.data?.series ?? []} currency={volume.data?.currency ?? "USD"} />
          )}
        </Sheet>

        <Sheet className="settle settle-3 overflow-hidden">
          <SheetHeader title="How customers pay" description="By value, last 30 days" />
          {volume.isLoading ? (
            <Skeleton rows={4} />
          ) : (
            <MethodBreakdown data={volume.data?.breakdown ?? []} currency={volume.data?.currency ?? "USD"} />
          )}
        </Sheet>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Sheet className="settle settle-4 overflow-hidden">
          <SheetHeader
            title="Recent payments"
            actions={
              <Link
                to="/payments"
                className="text-[12.5px] text-verdigris underline underline-offset-2"
              >
                View all
              </Link>
            }
          />
          {overview.isLoading ? (
            <Skeleton />
          ) : recent.length === 0 ? (
            <EmptyState
              title="No payments yet"
              description={
                mode === "sandbox"
                  ? "Create your first sandbox payment with the API keys from the integration section."
                  : "Live payments will appear here as your customers pay."
              }
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Reference</Th>
                  <Th>Method</Th>
                  <Th>Status</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">When</Th>
                </tr>
              </thead>
              <tbody>
                {recent.map((payment) => (
                  <PaymentRow key={payment.id} payment={payment} />
                ))}
              </tbody>
            </Table>
          )}
        </Sheet>

        <Sheet className="settle settle-5 overflow-hidden">
          <SheetHeader title="Awaiting payout" description="Cleared and owed to you" />
          {balances.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-ink-muted">
              Nothing is waiting to be settled.
            </p>
          ) : (
            <ul className="divide-y divide-rule">
              {balances.map((balance) => (
                <li key={balance.currency} className="flex items-baseline justify-between px-5 py-3.5">
                  <span className="font-mono text-[12px] text-ink-muted">{balance.currency}</span>
                  <span className="font-display text-[20px] tabular">{formatAmount(balance.minor)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-rule px-5 py-3">
            <Link to="/payouts" className="text-[12.5px] text-verdigris underline underline-offset-2">
              Payout history
            </Link>
          </div>
        </Sheet>
      </div>
    </>
  );
}

function PaymentRow({ payment }: { payment: Payment }) {
  return (
    <Tr>
      <Td>
        <Link to={`/payments/${payment.id}`} className="block">
          <span className="font-mono text-[12px] text-ink">{payment.reference}</span>
          {payment.item_name && (
            <span className="mt-0.5 block truncate text-[11.5px] text-ink-faint">
              {payment.item_name}
            </span>
          )}
        </Link>
      </Td>
      <Td className="text-ink-muted">{methodLabel(payment.method)}</Td>
      <Td>
        <StatusPill status={payment.status} />
      </Td>
      <Td align="right" mono>
        {payment.charged.formatted} <span className="text-ink-faint">{payment.currency}</span>
      </Td>
      <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
        {formatRelative(payment.created_at)}
      </Td>
    </Tr>
  );
}

/** VerificationPrompt tells an unapproved merchant exactly where they stand and what
 *  to do next, rather than leaving them to discover that live keys are refused. */
function VerificationPrompt({ status }: { status: string }) {
  const copy: Record<string, { title: string; body: string; cta: string }> = {
    pending_kyc: {
      title: "You are in sandbox",
      body: "Build and test against the sandbox freely. To accept real payments, complete business verification.",
      cta: "Start verification",
    },
    kyc_submitted: {
      title: "Verification submitted",
      body: "Our team is reviewing your documents. You will be able to issue live keys as soon as it is approved.",
      cta: "View submission",
    },
    kyc_in_review: {
      title: "Verification under review",
      body: "An operator is checking your documents now. Keep building against the sandbox in the meantime.",
      cta: "View submission",
    },
    rejected: {
      title: "Verification needs attention",
      body: "Your submission was not approved. Open it to see what needs correcting and resubmit.",
      cta: "Review and resubmit",
    },
    suspended: {
      title: "Account suspended",
      body: "Live payments are stopped. Contact Bushveld to resolve this.",
      cta: "View details",
    },
  };
  const content = copy[status] ?? copy.pending_kyc!;

  return (
    <div className="settle mb-6 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-brass/30 bg-brass-wash px-5 py-4">
      <div className="min-w-0">
        <p className="text-[13.5px] font-semibold text-ink">{content.title}</p>
        <p className="mt-0.5 max-w-2xl text-[12.5px] leading-relaxed text-ink-muted">
          {content.body}
        </p>
      </div>
      <Link
        to="/verification"
        className="shrink-0 rounded-md border border-brass/40 bg-paper-raised px-3 py-1.5 text-[12.5px] font-medium text-ink transition-colors hover:bg-paper"
      >
        {content.cta}
      </Link>
    </div>
  );
}

function greeting(name?: string): string {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { hour: "numeric", hour12: false, timeZone: "Africa/Harare" }).format(
      new Date(),
    ),
  );
  const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return name ? `${part}, ${name}` : part;
}
